import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { IoCalendarOutline, IoHelpOutline, IoOpenOutline } from 'react-icons/io5'
import {
  CloudFunctionResponseCodes,
  ServerAppSubscriptionStatusEnum,
  ServerAppSubscriptionType,
  useGeneratePaddleCustomerPortalSessionUrl,
} from '@expane/data'
import { useDateFormatting } from 'logic/hooks/useDateFormatting'
import { Button } from '@expane/ui'
import { useSnackbar } from '@expane/widgets'

export const CurrentAppSubscriptionInfo: FC<{
  activeSubscription: ServerAppSubscriptionType | undefined
}> = ({ activeSubscription }) => {
  const { t } = useTranslation()

  const [openSnackbar] = useSnackbar()

  const { mutateAsync: generatePaddleCustomerPortalSessionUrl, isLoading } =
    useGeneratePaddleCustomerPortalSessionUrl()

  const formatDate = useDateFormatting()

  const isExist = Boolean(activeSubscription)

  const className = getContainerClassName(isExist, activeSubscription?.status)
  const titleColor = getTitleColor(activeSubscription?.status)

  const openPaddleCustomerPortal = async () => {
    if (!activeSubscription) {
      openSnackbar(t('submitError'), 'error')

      return
    }
    try {
      const result = await generatePaddleCustomerPortalSessionUrl({
        referenceId: activeSubscription.referenceId,
      })

      if (
        result &&
        result.generatePaddleCustomerPortalSessionUrl?.code ===
          CloudFunctionResponseCodes.successful &&
        result.generatePaddleCustomerPortalSessionUrl.customerPortalSessionsUrl
      ) {
        window.open(
          result.generatePaddleCustomerPortalSessionUrl.customerPortalSessionsUrl,
          '_blank',
          'noopener,noreferrer',
        )

        return
      } else openSnackbar(t('submitError'), 'error')
    } catch (e) {
      reportError(e)
      openSnackbar(t('submitError'), 'error')
    }
  }

  const canManageSubscription = Boolean(
    activeSubscription && !activeSubscription.referenceId.includes('exp_'),
  )

  return (
    <div className={className}>
      {isExist ? (
        <>
          <IoCalendarOutline size="3.5rem" className={`mr-2 ${titleColor}`} />

          <div>
            <p className={`font-medium text-lg ${titleColor}`}>
              {t('appSubscription.current')} {t('planType.' + activeSubscription?.planType)}
            </p>
            {activeSubscription?.status === ServerAppSubscriptionStatusEnum.PastDue && (
              <p className={titleColor}>{t('appSubscription.pastDue')}</p>
            )}
            {activeSubscription?.status === ServerAppSubscriptionStatusEnum.Trialing && (
              <p className="text-main-color">{t('appSubscription.trialing')}</p>
            )}
            {activeSubscription?.canceledAt && (
              <p className="text-billing-secondary-color">
                {t('appSubscription.canceledAt')}{' '}
                {formatDate('dateTime', activeSubscription.canceledAt, true)}
              </p>
            )}
            {activeSubscription?.status === ServerAppSubscriptionStatusEnum.Paused &&
              activeSubscription?.pausedAt && (
                <p className="text-billing-secondary-color">
                  {t('appSubscription.pausedAt')}{' '}
                  {formatDate('dateTime', activeSubscription.pausedAt, true)}
                </p>
              )}
            {activeSubscription?.nextBilledAt && (
              <p className="text-billing-secondary-color">
                {t('appSubscription.nextBilledAt')}{' '}
                {formatDate('date', activeSubscription.nextBilledAt, true)}
              </p>
            )}
          </div>
          {canManageSubscription && (
            <Button
              className="ml-auto"
              type="outline"
              onClick={openPaddleCustomerPortal}
              disabled={isLoading}
              spinner={isLoading}
              Icon={IoOpenOutline}
              iconSide="right"
            >
              {t('appSubscription.manageSubscription')}
            </Button>
          )}
        </>
      ) : (
        <>
          <IoHelpOutline size="4rem" />
          <div>
            <p className="font-medium text-lg">{t('appSubscription.noPlan')}</p>
            <p>{t('appSubscription.pickOne')}</p>
          </div>
        </>
      )}
    </div>
  )
}

const getContainerClassName = (isExist: boolean, status?: ServerAppSubscriptionStatusEnum) => {
  let className = 'flex items-center border-2 rounded-lg p-4 mb-4'

  if (!isExist) {
    className += ' border-gray-400 border-dashed bg-gray-50 dark:bg-gray-700 text-label-color'
  } else {
    if (
      status === ServerAppSubscriptionStatusEnum.PastDue ||
      status === ServerAppSubscriptionStatusEnum.Paused
    ) {
      className += ' border-orange-300 bg-orange-100/10'
    } else {
      className += ' border-primary-400 bg-accent'
    }
  }

  return className
}

const getTitleColor = (status?: ServerAppSubscriptionStatusEnum) => {
  if (
    status === ServerAppSubscriptionStatusEnum.PastDue ||
    status === ServerAppSubscriptionStatusEnum.Paused
  ) {
    return 'text-orange-300'
  }

  return 'text-billing-main-color'
}
